import React from "react";
import styles from "./Footer.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons";
import {
  faPhoneAlt,
  faEnvelope,
  faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import { NavLink, useLocation } from "react-router-dom";

const Footer = () => {
  const location = useLocation();

  const handleBackToTop = () => {
    // Try multiple scroll methods for maximum compatibility
    
    // Method 1: Standard window scroll
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    // Method 2: Document scrolling element
    const scrollingElement = document.scrollingElement || document.documentElement;
    scrollingElement.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    // Method 3: Document body
    document.body.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    // Method 4: Find and scroll all scrollable containers
    const scrollableElements = document.querySelectorAll(
      'main, .content, #root, .app-container, .main-content, .site-header'
    );
    
    scrollableElements.forEach((element) => {
      if (element && element.scrollHeight > element.clientHeight) {
        element.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    });

    // Method 5: Fallback for Capacitor WebView
    if (window.Capacitor) {
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        
        // Also try scrollIntoView on header
        const header = document.querySelector('.site-header');
        if (header) {
          header.scrollIntoView({ 
            behavior: "smooth", 
            block: "start" 
          });
        }
      });
    }

    // Method 6: Final fallback with setTimeout
    setTimeout(() => {
      if (
        window.scrollY > 0 ||
        document.documentElement.scrollTop > 0 ||
        document.body.scrollTop > 0
      ) {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
    }, 500);
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  return (
    <footer className={styles.footer}>
      {/* Snowflake decoration */}
      <ul className={styles.snowflakes} aria-hidden="true">
        {Array.from({ length: 10 }).map((_, i) => (
          <li key={i} style={{ "--n": i }}>
            ❄
          </li>
        ))}
      </ul>

      {/* Main footer content */}
      <div className={styles.topSection}>

        {/* Explore */}
        <div className={styles.column}>
          <h4>Explore YetiDeals</h4>

          <ul>
            <li>
              <NavLink
                to="/about"
                className={`${styles.navLink} ${
                  isActiveRoute("/about") ? styles.activeLink : ""
                }`}
                aria-current={
                  isActiveRoute("/about") ? "page" : undefined
                }
              >
                About Us
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/faq"
                className={`${styles.navLink} ${
                  isActiveRoute("/faq") ? styles.activeLink : ""
                }`}
                aria-current={
                  isActiveRoute("/faq") ? "page" : undefined
                }
              >
                FAQs
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/terms"
                className={`${styles.navLink} ${
                  isActiveRoute("/terms") ? styles.activeLink : ""
                }`}
                aria-current={
                  isActiveRoute("/terms") ? "page" : undefined
                }
              >
                Terms
              </NavLink>
            </li>

            <li>
              <NavLink
                to="/privacy"
                className={`${styles.navLink} ${
                  isActiveRoute("/privacy") ? styles.activeLink : ""
                }`}
                aria-current={
                  isActiveRoute("/privacy") ? "page" : undefined
                }
              >
                Privacy Policy
              </NavLink>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div className={styles.column}>
          <h4>Contact</h4>

          <p>
            <a
              href="tel:+9779815014878"
              className={styles.externalLink}
              aria-label="Call YetiDeals"
            >
              <FontAwesomeIcon
                icon={faPhoneAlt}
                className={styles.icon}
                aria-hidden="true"
              />
              <span>+977-9815014878</span>
            </a>
          </p>

          <p>
            <a
              href="mailto:yetideals.np@gmail.com"
              className={styles.externalLink}
              aria-label="Email YetiDeals"
            >
              <FontAwesomeIcon
                icon={faEnvelope}
                className={styles.icon}
                aria-hidden="true"
              />
              <span>yetideals.np@gmail.com</span>
            </a>
          </p>
        </div>

        {/* Social */}
        <div className={styles.column}>
          <h4>Follow Us</h4>

          <div className={styles.social}>
            <a
              href="https://www.facebook.com/profile.php?id=61578282171426"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialLink}
              aria-label="YetiDeals on Facebook"
            >
              <FontAwesomeIcon
                icon={faFacebookF}
                aria-hidden="true"
              />
            </a>

            <a
              href="https://www.instagram.com/yetideals.np/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.socialLink}
              aria-label="YetiDeals on Instagram"
            >
              <FontAwesomeIcon
                icon={faInstagram}
                aria-hidden="true"
              />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom section */}
      <div className={styles.bottom}>
        <span>
          © {new Date().getFullYear()} YetiDeals. All rights reserved.
        </span>

        <button
          type="button"
          className={styles.backToTop}
          onClick={handleBackToTop}
          aria-label="Back to top"
        >
          <span>Back to top</span>

          <FontAwesomeIcon
            icon={faArrowUp}
            aria-hidden="true"
          />
        </button>
      </div>
    </footer>
  );
};

export default Footer;